import React, { useState, useEffect } from 'react';
import { 
  Layers, 
  Plus, 
  Trash2, 
  Edit3, 
  Save, 
  Clock, 
  Upload, 
  Image as ImageIcon, 
  Eye, 
  X,
  Link2,
  ExternalLink
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function EducationProgramLevelsForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: 'Academic Pathways',
    title: 'Preschool, Kindergarten, Primary, and Secondary',
    subtitle: 'Each stage is thoughtfully designed to meet students at their level while growing faith, skills, and confidence.',
    levels: []
  });
  const [originalData, setOriginalData] = useState({
    kicker: 'Academic Pathways',
    title: 'Preschool, Kindergarten, Primary, and Secondary',
    subtitle: 'Each stage is thoughtfully designed to meet students at their level while growing faith, skills, and confidence.',
    levels: []
  });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [pendingLevels, setPendingLevels] = useState({});
  const [pendingHeader, setPendingHeader] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [editingLevelIndex, setEditingLevelIndex] = useState(null);
  const [modalLevelData, setModalLevelData] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);
  const [newHighlight, setNewHighlight] = useState('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/education/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[EducationProgramLevelsForm] Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    if (mediaMap[mediaId]) return mediaMap[mediaId];
    if (mediaMap[String(mediaId)]) return mediaMap[String(mediaId)];
    return resolveMediaSrc(mediaId, mediaMap) || mediaId;
  };

  const loadData = async () => {
    await Promise.all([loadMediaMap(), loadSection()]);
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let levelSection = null;
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const authHeaders = { 'Content-Type': 'application/json' };
      if (savedToken) {
        authHeaders['Authorization'] = `Bearer ${savedToken}`;
      }
      
      const adminResponse = await fetch(
        `${apiBase}/education/cms/sections`,
        { 
          credentials: 'include',
          headers: authHeaders
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          levelSection = result.data.find(s => s.section_type === 'education-program-levels-shell');
        }
      }
      
      if (!levelSection) {
        const publicResponse = await fetch(
          `${apiBase}/education/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: authHeaders
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            levelSection = result.data.find(s => s.section_type === 'education-program-levels-shell');
          }
        }
      }
      
      if (!levelSection) {
        throw new Error('Academic Pathways section not found');
      }

      const currentSectionId = levelSection.id;
      setSectionId(currentSectionId);

      let published = levelSection.content_published;
      if (typeof published === 'string') {
        try { published = JSON.parse(published); } catch (e) { published = {}; }
      }
      const en = published?.en || {};

      const initialKicker = en.kicker || 'Academic Pathways';
      const initialTitle = en.title || 'Preschool, Kindergarten, Primary, and Secondary';
      const initialSubtitle = en.subtitle || en.description || 'Each stage is thoughtfully designed to meet students at their level while growing faith, skills, and confidence.';

      const rawInitialLevels = Array.isArray(en.levels)
        ? [...en.levels]
        : (Array.isArray(en.program_levels?.levels) ? [...en.program_levels.levels] : []);
      const initialLevels = rawInitialLevels.filter(lvl => {
        if (!lvl || typeof lvl !== 'object') return false;
        if (lvl.delete !== undefined || lvl.deleted === true || lvl._deleted === true) return false;
        const title = (lvl.title || '').trim().toLowerCase();
        if (!title || title === 'xxx') return false;
        return true;
      }).map(lvl => ({
        ...lvl,
        link_url: lvl.link_url !== undefined ? lvl.link_url : (lvl.external_url || lvl.slug || '')
      }));

      const initialData = {
        kicker: initialKicker,
        title: initialTitle,
        subtitle: initialSubtitle,
        levels: initialLevels
      };

      const pendingMap = {};
      const pendingHeaderMap = {};

      try {
        const pendingRes = await fetch(`${apiBase}/cms/section/${currentSectionId}/with-pending`, {
          credentials: 'include',
          headers: authHeaders
        });
        if (pendingRes.ok) {
          const pendingData = await pendingRes.json();
          if (pendingData.success && Array.isArray(pendingData.data?.pending_changes)) {
            pendingData.data.pending_changes.forEach(ch => {
              if (ch.status === 'pending' && (ch.locale === 'en' || !ch.locale)) {
                let val = ch.new_value;
                if (typeof val === 'string') {
                  try { val = JSON.parse(val); } catch (e) {}
                }
                const match = ch.field_id.match(/^levels\.(\d+)\./);
                if (match) {
                  const idx = parseInt(match[1], 10);
                  pendingMap[idx] = true;
                  if (val && initialData.levels[idx]) {
                    initialData.levels[idx] = { ...initialData.levels[idx], ...val };
                  }
                } else if (['kicker', 'title', 'subtitle', 'description'].includes(ch.field_id)) {
                  pendingHeaderMap[ch.field_id] = true;
                  initialData[ch.field_id] = val;
                }
              }
            });
          }
        }
      } catch (pErr) {
        console.warn('Pending levels check error:', pErr);
      }

      setFormData(initialData);
      setOriginalData(initialData);
      setPendingLevels(pendingMap);
      setPendingHeader(pendingHeaderMap);
    } catch (err) {
      console.error('[EducationProgramLevelsForm] Load error:', err);
      toast.error('Error loading academic pathways: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmitHeaderField = async (fieldName, label) => {
    if (!sectionId) {
      toast.error('Academic Pathways section ID not loaded');
      return;
    }

    if (JSON.stringify(formData[fieldName]) === JSON.stringify(originalData[fieldName])) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldName,
          field_name: label || fieldName.replace(/_/g, ' '),
          new_value: formData[fieldName],
          old_value: originalData[fieldName],
          locale: 'en'
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`${label || fieldName} submitted for approval`);
        setPendingHeader(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleCloseModal = () => {
    if (editingLevelIndex !== null && editingLevelIndex >= originalData.levels.length) {
      setFormData(prev => ({
        ...prev,
        levels: prev.levels.filter((_, i) => i !== editingLevelIndex)
      }));
    }
    setEditingLevelIndex(null);
    setModalLevelData(null);
    setNewHighlight('');
  };

  const handleAddLevel = () => {
    const newLevel = {
      title: '',
      subtitle: '',
      description: '',
      image: '',
      link_url: '',
      slug: '',
      external_url: '',
      anchor: '',
      highlights: []
    };
    const nextIdx = formData.levels.length;
    setFormData(prev => ({
      ...prev,
      levels: [...prev.levels, newLevel]
    }));
    setEditingLevelIndex(nextIdx);
    setModalLevelData(newLevel);
    setNewHighlight('');
  };

  const handleDeleteLevel = async (index) => {
    if (pendingLevels[index]) {
      toast.info('This level is awaiting approval');
      return;
    }

    const levelToDelete = formData.levels[index];
    if (!levelToDelete) return;

    if (!window.confirm(`Delete program level: ${levelToDelete.title || index + 1}?`)) return;

    setSubmitting(prev => ({ ...prev, [`del_${index}`]: true }));
    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `levels.${index}.delete`,
          field_name: `Delete Program Level - ${levelToDelete.title || index + 1}`,
          old_value: levelToDelete,
          new_value: null,
          locale: 'en'
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`${levelToDelete.title || 'Level'} deletion submitted for approval`);
        setPendingLevels(prev => ({ ...prev, [index]: true }));
      } else {
        toast.error(result.error || 'Delete submission failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [`del_${index}`]: false }));
    }
  };

  const handleModalDataChange = (field, value) => {
    setModalLevelData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLinkChange = (value) => {
    const raw = value || '';
    const trimmed = raw.trim();
    const isExternal = trimmed.startsWith('http://') || trimmed.startsWith('https://');

    setModalLevelData(prev => ({
      ...prev,
      link_url: raw,
      external_url: isExternal ? trimmed : '',
      slug: isExternal ? '' : trimmed.replace(/^\//, ''),
      anchor: prev?.anchor || (isExternal ? '' : trimmed.replace(/^\//, '').split('#')[0])
    }));
  };

  const handleAddHighlight = () => {
    if (!newHighlight.trim()) return;
    setModalLevelData(prev => ({
      ...prev,
      highlights: [...(prev.highlights || []), newHighlight.trim()]
    }));
    setNewHighlight('');
  };

  const handleRemoveHighlight = (hIdx) => {
    setModalLevelData(prev => ({
      ...prev,
      highlights: (prev.highlights || []).filter((_, i) => i !== hIdx)
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (ev) => {
        if (ev.lengthComputable) {
          const percent = Math.round((ev.loaded / ev.total) * 100);
          setImageProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              if (mediaId) {
                handleModalDataChange('image', mediaId);
                toast.success('Level image uploaded successfully');
              } else {
                toast.error('No media ID returned');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            toast.error('Invalid server response');
          }
        } else {
          toast.error(`Upload failed (HTTP ${xhr.status})`);
        }
        setImageUploading(false);
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload failed - network error');
        setImageUploading(false);
      });

      xhr.open('POST', `${apiBase}/education/cms/media/upload?section_type=education-program-levels-shell`);
      xhr.withCredentials = true;
      if (savedToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${savedToken}`);
      }
      xhr.send(formDataToUpload);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      setImageUploading(false);
    }
  };

  const handleSubmitModal = async () => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const levelIndex = editingLevelIndex;
    const levelKey = `level_${levelIndex}`;

    if (!modalLevelData || !modalLevelData.title?.trim() || !modalLevelData.description?.trim()) {
      toast.warning('Please enter both Title and Description');
      return;
    }

    const currentLink = (modalLevelData.link_url !== undefined
      ? modalLevelData.link_url
      : (modalLevelData.external_url || modalLevelData.slug || '')
    ).trim();

    const isExternal = currentLink.startsWith('http://') || currentLink.startsWith('https://');

    const cleanLevel = {
      ...modalLevelData,
      link_url: currentLink,
      external_url: isExternal ? currentLink : '',
      slug: isExternal ? '' : currentLink.replace(/^\//, ''),
      anchor: modalLevelData.anchor || (isExternal ? '' : currentLink.replace(/^\//, '').split('#')[0])
    };

    setSubmitting(prev => ({ ...prev, [levelKey]: true }));

    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const isNew = levelIndex >= originalData.levels.length;
      const op = isNew ? 'add' : 'update';

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `levels.${levelIndex}.${op}`,
            field_name: `${isNew ? 'Add' : 'Update'} Academic Level - ${cleanLevel.title}`,
            old_value: isNew ? null : (originalData.levels[levelIndex] || {}),
            new_value: cleanLevel,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Level "${cleanLevel.title}" submitted for approval`);
        setPendingLevels(prev => ({ ...prev, [levelIndex]: true }));
        setFormData(prev => ({
          ...prev,
          levels: prev.levels.map((l, i) => i === levelIndex ? cleanLevel : l)
        }));
        setOriginalData(prev => {
          const next = [...prev.levels];
          next[levelIndex] = cleanLevel;
          return { ...prev, levels: next };
        });
        setEditingLevelIndex(null);
        setModalLevelData(null);
      } else {
        if (response.status === 409) {
          toast.error('This level already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [levelKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Academic Pathways...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <Layers size={22} />
          </div>
          <div>
            <h2 className="unified-title">Academic Pathways</h2>
            <p className="unified-subtitle">Manage pathways and education stages from Preschool and Kindergarten through Primary and Secondary</p>
          </div>
        </div>
      </div>

      {/* Section Header Editors */}
      <div style={{ marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {/* Section Tag / Kicker */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <label className="unified-label">Section Tag / Kicker</label>
            {pendingHeader.kicker && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="text"
              maxLength="60"
              className="unified-input"
              value={formData.kicker}
              onChange={(e) => handleFieldChange('kicker', e.target.value)}
              disabled={pendingHeader.kicker || submitting.kicker}
              placeholder="e.g. Academic Pathways"
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitHeaderField('kicker', 'Section Kicker')}
              disabled={submitting.kicker || pendingHeader.kicker || formData.kicker === originalData.kicker}
            >
              <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Section Headline */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <label className="unified-label">Section Headline</label>
            {pendingHeader.title && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="text"
              maxLength="120"
              className="unified-input"
              value={formData.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              disabled={pendingHeader.title || submitting.title}
              placeholder="e.g. Preschool, Kindergarten, Primary, and Secondary"
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitHeaderField('title', 'Section Headline')}
              disabled={submitting.title || pendingHeader.title || formData.title === originalData.title}
            >
              <Save size={14} /> {submitting.title ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Section Subtitle / Description */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <label className="unified-label">Section Subtitle / Description</label>
            {pendingHeader.subtitle && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>
          <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
            <textarea
              maxLength="500"
              className="unified-textarea"
              rows="3"
              value={formData.subtitle}
              onChange={(e) => handleFieldChange('subtitle', e.target.value)}
              disabled={pendingHeader.subtitle || submitting.subtitle}
              placeholder="e.g. Each stage is thoughtfully designed to meet students at their level while growing faith, skills, and confidence."
            />
            <button
              type="button"
              className="unified-submit-button"
              style={{ marginTop: '2px' }}
              onClick={() => handleSubmitHeaderField('subtitle', 'Section Subtitle')}
              disabled={submitting.subtitle || pendingHeader.subtitle || formData.subtitle === originalData.subtitle}
            >
              <Save size={14} /> {submitting.subtitle ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Action Row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
            Academic Stages & Levels ({formData.levels.length})
          </h3>
          <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0' }}>
            Academic pathways and developmental stages offered at Sekolah Permata Harapan
          </p>
        </div>
        <button
          type="button"
          className="unified-submit-button"
          onClick={handleAddLevel}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          <Plus size={16} /> Add Program Level
        </button>
      </div>

      {/* Levels list (Vertical downward stack) */}
      {formData.levels.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f8fafc', borderRadius: '14px', border: '2px dashed #cbd5e1' }}>
          <Layers size={38} color="#94a3b8" style={{ margin: '0 auto 12px' }} />
          <h4 style={{ fontSize: '15px', color: '#334155', margin: '0 0 4px', fontWeight: '700' }}>No Program Levels Found</h4>
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Click "Add Program Level" above to create an academic level</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {formData.levels.map((level, idx) => {
            const isPending = pendingLevels[idx];
            const photoUrl = getMediaUrl(level.image);
            const highlightsCount = Array.isArray(level.highlights) ? level.highlights.length : 0;

            return (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '22px',
                  padding: '20px 24px',
                  borderRadius: '14px',
                  border: isPending ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                  borderLeft: isPending ? '5px solid #f59e0b' : `5px solid ${idx % 2 === 0 ? '#2563eb' : '#6366f1'}`,
                  background: isPending ? '#fffdf7' : '#ffffff',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Photo Thumbnail */}
                <div
                  style={{
                    width: '120px',
                    height: '85px',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    flexShrink: 0,
                    background: '#0f172a',
                    border: '1px solid #cbd5e1',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {photoUrl ? (
                    <img
                      src={photoUrl}
                      alt={level.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => setLightboxImage({ src: photoUrl, title: level.title })}
                    />
                  ) : (
                    <div style={{ color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                      <ImageIcon size={22} />
                      <span style={{ fontSize: '10px' }}>No photo</span>
                    </div>
                  )}
                  {photoUrl && (
                    <button
                      type="button"
                      onClick={() => setLightboxImage({ src: photoUrl, title: level.title })}
                      style={{
                        position: 'absolute',
                        bottom: '4px',
                        right: '4px',
                        background: 'rgba(0,0,0,0.65)',
                        color: '#fff',
                        border: 'none',
                        padding: '3px 5px',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      title="View Photo"
                    >
                      <Eye size={11} />
                    </button>
                  )}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>
                      {level.title || `Level ${idx + 1}`}
                    </h4>
                    {level.subtitle && (
                      <span
                        style={{
                          padding: '2px 10px',
                          borderRadius: '6px',
                          background: '#eff6ff',
                          color: '#1d4ed8',
                          fontSize: '11.5px',
                          fontWeight: '700'
                        }}
                      >
                        {level.subtitle}
                      </span>
                    )}
                    {isPending && (
                      <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                        <Clock size={10} /> Pending Review
                      </span>
                    )}
                  </div>
                  <p
                    style={{
                      margin: '0 0 6px',
                      fontSize: '13px',
                      color: '#475569',
                      lineHeight: 1.5,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {level.description || 'No description entered yet.'}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
                    {/* Destination Link Badge */}
                    {level.external_url ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '4px', fontSize: '11.5px', fontWeight: '600' }}>
                        <ExternalLink size={12} /> {level.external_url}
                      </span>
                    ) : (level.link_url || level.slug) ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#ecfdf5', color: '#047857', padding: '2px 8px', borderRadius: '4px', fontSize: '11.5px', fontWeight: '600' }}>
                        <Link2 size={12} /> Target: /{(level.link_url || level.slug).replace(/^\//, '')}
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#94a3b8', fontStyle: 'italic', fontSize: '11.5px' }}>
                        <Link2 size={12} /> No link configured
                      </span>
                    )}

                    {highlightsCount > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '11.5px', color: '#64748b', fontWeight: '600' }}>Highlights:</span>
                        {level.highlights.slice(0, 3).map((hl, hIdx) => (
                          <span key={hIdx} style={{ fontSize: '11px', background: '#f1f5f9', color: '#334155', padding: '1px 8px', borderRadius: '4px' }}>
                            • {hl}
                          </span>
                        ))}
                        {highlightsCount > 3 && (
                          <span style={{ fontSize: '11px', color: '#94a3b8' }}>+{highlightsCount - 3} more</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <button
                    type="button"
                    className="unified-secondary-btn"
                    onClick={() => {
                      if (isPending) {
                        toast.info('This level is awaiting approval');
                        return;
                      }
                      const existingLink = (level.link_url || level.external_url || level.slug || '').trim();
                      setEditingLevelIndex(idx);
                      setModalLevelData({
                        ...level,
                        link_url: existingLink,
                        highlights: Array.isArray(level.highlights) ? [...level.highlights] : []
                      });
                      setNewHighlight('');
                    }}
                    disabled={isPending}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  >
                    <Edit3 size={14} /> Edit Level
                  </button>
                  {!isPending && (
                    <button
                      type="button"
                      onClick={() => handleDeleteLevel(idx)}
                      disabled={submitting[`del_${idx}`]}
                      style={{
                        padding: '8px 12px',
                        color: '#ef4444',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center'
                      }}
                      title="Delete Level"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Level Modal */}
      {editingLevelIndex !== null && modalLevelData && (
        <div className="unified-modal-overlay" onClick={handleCloseModal}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
                  {modalLevelData.title ? `Edit: ${modalLevelData.title}` : 'Add Program Level'}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Configure title, age group, descriptive summary, and developmental highlights
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body">
              {/* Image Preview & Upload */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ width: '120px', height: '85px', borderRadius: '8px', overflow: 'hidden', background: '#0f172a', border: '1px solid #cbd5e1', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {modalLevelData.image && getMediaUrl(modalLevelData.image) ? (
                    <img
                      src={getMediaUrl(modalLevelData.image)}
                      alt="Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <ImageIcon size={28} color="#94a3b8" />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <label className="unified-label" style={{ marginBottom: '6px' }}>Level Photography</label>
                  <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#64748b' }}>
                    JPG, PNG, WebP • Max 5MB • Recommended: 600×400px
                  </p>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <input
                      type="file"
                      id="level-photo-upload"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageUpload}
                      disabled={imageUploading}
                      style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                    />
                    <label htmlFor="level-photo-upload" className="unified-secondary-btn" style={{ cursor: 'pointer' }}>
                      <Upload size={14} /> {imageUploading ? `Uploading ${imageProgress}%...` : 'Browse & Upload Photo'}
                    </label>
                  </div>
                </div>
              </div>

              {/* Title */}
              <div style={{ marginBottom: '16px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Level Title</label>
                <input
                  type="text"
                  className="unified-input"
                  value={modalLevelData.title || ''}
                  onChange={(e) => handleModalDataChange('title', e.target.value)}
                  placeholder="e.g. Primary School / Sekolah Dasar"
                />
              </div>

              {/* Subtitle / Age */}
              <div style={{ marginBottom: '16px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Age Group / Grades</label>
                <input
                  type="text"
                  className="unified-input"
                  value={modalLevelData.subtitle || ''}
                  onChange={(e) => handleModalDataChange('subtitle', e.target.value)}
                  placeholder="e.g. Ages 6-12 • Grades 1 to 6"
                />
              </div>

              {/* Description */}
              <div style={{ marginBottom: '20px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Level Overview & Pedagogy</label>
                <textarea
                  className="unified-textarea"
                  rows="4"
                  value={modalLevelData.description || ''}
                  onChange={(e) => handleModalDataChange('description', e.target.value)}
                  placeholder="Comprehensive description of curriculum goals and development at this stage..."
                />
              </div>

              {/* Destination Link */}
              <div style={{ marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <label className="unified-label" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', margin: 0 }}>
                    <Link2 size={15} color="#2563eb" /> Card Destination / Target Link
                  </label>
                  {(modalLevelData.link_url || modalLevelData.external_url || modalLevelData.slug) && (
                    <span style={{ fontSize: '11.5px', color: (modalLevelData.link_url || modalLevelData.external_url || '').startsWith('http') ? '#0284c7' : '#059669', fontWeight: '600' }}>
                      {(modalLevelData.link_url || modalLevelData.external_url || '').startsWith('http') ? 'External Website' : 'Internal Page Route'}
                    </span>
                  )}
                </div>

                {/* Quick Presets */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  <span style={{ fontSize: '11.5px', color: '#64748b', fontWeight: '600' }}>Quick Presets:</span>
                  {[
                    { label: 'Preschool', value: 'preschool' },
                    { label: 'Kindergarten', value: 'kindergarten' },
                    { label: 'Primary', value: 'primary' },
                    { label: 'Secondary', value: 'secondary' },
                    { label: 'Admissions', value: 'admissions' }
                  ].map(preset => {
                    const currentVal = (modalLevelData.link_url !== undefined ? modalLevelData.link_url : (modalLevelData.slug || modalLevelData.external_url || '')).replace(/^\//, '');
                    const isSelected = currentVal === preset.value;
                    return (
                      <button
                        key={preset.value}
                        type="button"
                        onClick={() => handleLinkChange(preset.value)}
                        style={{
                          padding: '4px 10px',
                          fontSize: '11.5px',
                          fontWeight: isSelected ? '700' : '500',
                          color: isSelected ? '#1d4ed8' : '#334155',
                          background: isSelected ? '#dbeafe' : '#ffffff',
                          border: isSelected ? '1px solid #93c5fd' : '1px solid #cbd5e1',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        {preset.label}
                      </button>
                    );
                  })}
                  {(modalLevelData.link_url || modalLevelData.slug || modalLevelData.external_url) && (
                    <button
                      type="button"
                      onClick={() => handleLinkChange('')}
                      style={{
                        padding: '4px 8px',
                        fontSize: '11px',
                        color: '#ef4444',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                      title="Clear destination link"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* Link input */}
                <input
                  type="text"
                  className="unified-input"
                  value={modalLevelData.link_url !== undefined ? modalLevelData.link_url : (modalLevelData.external_url || modalLevelData.slug || '')}
                  onChange={(e) => handleLinkChange(e.target.value)}
                  placeholder="e.g. secondary, preschool, /admissions, or https://example.com"
                />

                <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#64748b', lineHeight: 1.4 }}>
                  Enter an internal page slug (e.g. <code>secondary</code>, <code>preschool</code>) or external URL (starts with <code>http://</code> or <code>https://</code>). When students or visitors click this card, it navigates to this destination.
                </p>

                {/* Preview / Test */}
                {(modalLevelData.link_url || modalLevelData.external_url || modalLevelData.slug) && (
                  <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {(modalLevelData.link_url || modalLevelData.external_url || '').startsWith('http') ? (
                      <a
                        href={modalLevelData.link_url || modalLevelData.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          fontSize: '12px',
                          color: '#0284c7',
                          textDecoration: 'none',
                          fontWeight: '600'
                        }}
                      >
                        <ExternalLink size={13} /> Test Link in New Tab
                      </a>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#059669', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Link2 size={13} /> Target route: <strong>/{(modalLevelData.link_url || modalLevelData.slug || '').replace(/^\//, '')}</strong>
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Highlights */}
              <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Key Program Highlights</label>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                  {(modalLevelData.highlights || []).map((hl, hIdx) => (
                    <div
                      key={hIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 12px',
                        background: '#ffffff',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px'
                      }}
                    >
                      <span>• {hl}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveHighlight(hIdx)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px' }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    className="unified-input"
                    value={newHighlight}
                    onChange={(e) => setNewHighlight(e.target.value)}
                    placeholder="Add a highlight bullet point..."
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddHighlight(); } }}
                  />
                  <button
                    type="button"
                    className="unified-secondary-btn"
                    onClick={handleAddHighlight}
                    disabled={!newHighlight.trim()}
                  >
                    <Plus size={14} /> Add
                  </button>
                </div>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleSubmitModal}
                disabled={submitting[`level_${editingLevelIndex}`] || !modalLevelData.title?.trim() || imageUploading}
              >
                <Save size={14} /> {submitting[`level_${editingLevelIndex}`] ? 'Submitting...' : 'Save & Submit Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-14px',
                right: '-14px',
                background: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                cursor: 'pointer'
              }}
            >
              <X size={18} color="#0f172a" />
            </button>
            <img
              src={lightboxImage.src}
              alt={lightboxImage.title || 'Level photo'}
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.5)' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
